// src/features/equipment/components/maintenance/ServiceProviderContact.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, Tooltip, Typography } from '@mui/material';
import {
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';

import { contactsAPI } from '../../../contacts/services/contacts';
import { usePermissions } from '../../../auth/hooks/usePermissions';

/**
 * Technician of a case. When it was picked from Contacts, the contact's phone
 * numbers and emails are shown as call / email buttons. The contact is loaded
 * through the contacts API, so `contact:read` decides who sees them.
 */
export default function ServiceProviderContact({ maintenanceCase: c }) {
  const { t } = useTranslation(['equipment']);
  const permissions = usePermissions();
  const canReadContacts = permissions.hasScope('contact:read');
  const contactId = c.service_provider_contact_id;
  const [contact, setContact] = useState(null);

  useEffect(() => {
    setContact(null);
    if (!contactId || !canReadContacts) return undefined;
    let cancelled = false;
    contactsAPI.getById(contactId)
      .then((response) => {
        if (!cancelled) setContact(response.data?.data ?? null);
      })
      // A deleted or hidden contact just leaves the plain name
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contactId, canReadContacts]);

  if (!c.service_provider) return null;

  const phones = contact?.phone_numbers ?? [];
  const emails = contact?.emails ?? [];
  const subject = encodeURIComponent(`${c.equipment.name}: ${c.title}`);

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {t('equipment:maintenance.fields.service_provider')}
      </Typography>
      {contact ? (
        <Link component={RouterLink} to={`/contacts/${contact.contact_id}`} variant="body2">
          {c.service_provider}
        </Link>
      ) : (
        <Typography variant="body2">{c.service_provider}</Typography>
      )}
      {(phones.length > 0 || emails.length > 0) && (
        <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
          {phones.map(phone => (
            <Tooltip key={phone.phone_id} title={phone.label || ''}>
              <Button size="small" variant="outlined" startIcon={<PhoneIcon />}
                      href={`tel:${phone.country_code}${phone.number.replace(/\s/g, '')}`}>
                {`${phone.country_code} ${phone.number}`}
              </Button>
            </Tooltip>
          ))}
          {emails.map(email => (
            <Tooltip key={email.email_id} title={email.email}>
              <Button size="small" variant="outlined" startIcon={<EmailIcon />} endIcon={<OpenInNewIcon />}
                      href={`mailto:${email.email}?subject=${subject}`}>
                {/* With several addresses the button has to say which one */}
                {emails.length > 1 ? email.email : t('equipment:maintenance.actions.send_email')}
              </Button>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
}
